import Fade from '@components/Animation/Fade';
import GalleryPaging from '@components/Gallery/GalleryPaging';
import GalleryText from '@components/Gallery/GalleryText';
import Analytics from '@services/analytics/Analytics';
import {getRefElement} from '@utils/refUtils';
import throttle from 'lodash.throttle';
import type {MouseEvent, ReactNode, RefObject, SyntheticEvent} from 'react';
import {Component, createRef} from 'react';
import {TransitionGroup} from 'react-transition-group';

const DEFAULT_IMAGE_HEIGHT = 500;
const THROTTLE_INTERVAL = 300;
const IMAGE_CLICK_WIDTH_THRESHOLD = 2;

interface GalleryItem {
    image: string;
    caption?: string;
    content: string;
}

interface Props {
    children: HTMLDivElement[];
}

interface State {
    displayedElement: number;
    imageHeight: number;
    textHeight: number;
    items: GalleryItem[];
}

export default class Gallery extends Component<Props, State> {
    public readonly state: State = {
        displayedElement: 0,
        imageHeight: DEFAULT_IMAGE_HEIGHT,
        textHeight: 0,
        items: this.parseChildren(),
    };

    private readonly textRef: RefObject<HTMLDivElement> = createRef();
    private readonly imageRef: RefObject<HTMLImageElement> = createRef();

    private readonly handleResize = throttle(() => {
        try {
            const image = getRefElement(this.imageRef);
            this.setState({
                imageHeight: image.offsetHeight,
            });
        } catch (refError) {
            console.error(refError);
        }

        try {
            const text = getRefElement(this.textRef);
            this.setState({
                textHeight: text.offsetHeight,
            });
        } catch (refError) {
            console.error(refError);
        }
    }, THROTTLE_INTERVAL);

    public componentDidMount() {
        window.addEventListener('resize', this.handleResize);
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    private readonly handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
        this.setState({imageHeight: event.currentTarget.offsetHeight});
    };

    private readonly handleImageClick = (event: MouseEvent<HTMLImageElement>) => {
        if (
            event.nativeEvent.offsetX <
            event.currentTarget.offsetWidth / IMAGE_CLICK_WIDTH_THRESHOLD
        ) {
            this.switchBack();
        } else {
            this.switchForward();
        }
        Analytics.triggerEvent('Переключение галереи нажатием на картинку', 'click');
    };

    private readonly handlePreviousClick = () => {
        this.switchBack();
        Analytics.triggerEvent('Переключение галереи нажатием на кнопку', 'click');
    };

    private readonly handleNextClick = () => {
        this.switchForward();
        Analytics.triggerEvent('Переключение галереи нажатием на кнопку', 'click');
    };

    private readonly handleHeight = (height: number) => {
        this.setState({textHeight: height});
    };

    private parseChildren(): GalleryItem[] {
        const result = [];
        for (const item of this.props.children as HTMLDivElement[]) {
            result.push({
                image: item.querySelector('img')!.getAttribute('src')!,
                caption: item.querySelector('.gallery_caption')!.textContent ?? '',
                content: item.querySelector('.gallery_text_raw')!.innerHTML,
            });
        }
        return result;
    }

    private switchBack() {
        this.setState(prevState => ({
            displayedElement:
                prevState.displayedElement <= 0
                    ? this.props.children.length - 1
                    : prevState.displayedElement - 1,
        }));
    }

    private switchForward() {
        this.setState(prevState => ({
            displayedElement:
                prevState.displayedElement + 1 >= this.props.children.length
                    ? 0
                    : prevState.displayedElement + 1,
        }));
    }

    public render(): ReactNode {
        const currentPage = this.state.displayedElement + 1;
        const totalPages = this.props.children.length;
        return (
            <div className="gallery_contents">
                <TransitionGroup
                    className="gallery_image_wrapper"
                    style={{height: this.state.imageHeight}}
                >
                    <Fade key={this.state.displayedElement}>
                        <img
                            className="gallery_image"
                            src={this.state.items[this.state.displayedElement].image}
                            onClick={this.handleImageClick}
                            onLoad={this.handleImageLoad}
                            ref={this.imageRef}
                            loading="lazy"
                        />
                    </Fade>
                </TransitionGroup>
                <div className="gallery_padder">
                    <div className="gallery_caption">
                        {this.state.items[this.state.displayedElement].caption}
                    </div>
                    <TransitionGroup
                        className="gallery_text_wrapper"
                        style={{height: this.state.textHeight}}
                    >
                        <Fade key={this.state.displayedElement}>
                            <GalleryText
                                contents={this.state.items[this.state.displayedElement].content}
                                onHeight={this.handleHeight}
                                textRef={this.textRef}
                            />
                        </Fade>
                    </TransitionGroup>
                    <GalleryPaging
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onNextClick={this.handleNextClick}
                        onPreviousClick={this.handlePreviousClick}
                    />
                </div>
            </div>
        );
    }
}
